import { FC, useEffect, useRef } from 'react'

import EmptyState from '@/components/CommonState/EmptyState'
import LoadingState from '@/components/CommonState/LoadingState'
import { Personal } from '@/components/Personal'
import { ISearchAuthorItem } from '@/api'
import FollowButton from '@/components/FollowButton'
import { useUserAtom } from '@/store'

import { cn, customJumpHref } from '@/utils'

export interface IPeopleResultProps {
  showHistory?: boolean
  data: ISearchAuthorItem[]
  loading?: boolean
  loadingMore?: boolean
  noMore?: boolean
  onLoadMore?: () => void
}

const PeopleResult: FC<IPeopleResultProps> = (props) => {
  const { showHistory, data, loading, loadingMore, noMore, onLoadMore } = props
  const { user } = useUserAtom()
  const lastTriggerScrollTopRef = useRef(-1)

  useEffect(() => {
    if (!data.length) {
      lastTriggerScrollTopRef.current = -1
    }
  }, [data])

  return (
    <div
      id='peopleResult'
      className={cn('relative h-[calc(100dvh-62px-104px-37px-37px)] overflow-y-scroll')}
      onScroll={(e) => {
        const target = e.currentTarget
        const scrollTop = target.scrollTop
        const distanceToBottom = target.scrollHeight - (scrollTop + target.clientHeight)
        const isNearBottom = distanceToBottom <= 50

        if (!isNearBottom || loadingMore || noMore) return
        if (scrollTop <= lastTriggerScrollTopRef.current) return

        lastTriggerScrollTopRef.current = scrollTop
        onLoadMore?.()
      }}
    >
      {data.length ? (
        <>
          {data.map((item, index) => {
            return (
              <div
                className={cn(
                  'relative flex items-center justify-between px-[37px] py-[24px]',
                  'md:mb-0 md:flex-1 md:cursor-pointer md:items-center md:transition-all md:duration-300 md:hover:bg-color-white-trans10',
                )}
                key={`${item.user_id}-${index}`}
              >
                <Personal
                  size={'large'}
                  nickname={item.nickname}
                  avatar={item.avatar}
                  className={'flex-1'}
                  extra={
                    <div className={cn('font-geologica text-[22px] font-light leading-[30px] text-base-text-color opacity-40')}>
                      <span>{item.follower_count} Followers</span>
                    </div>
                  }
                  userData={{ id: item.user_id }}
                  avatarClassName={cn('h-[104px] w-[104px] shrink-0 rounded-full border-[2px] border-solid border-color-white-trans20')}
                  nickNameClassName={cn('mb-[11px] mr-[10px] font-geologica text-[30px] font-normal leading-none')}
                  onClickIntro={() => {
                    if (!['system', 'assistant', user.id].includes(item.user_id)) {
                      customJumpHref(`/people/${item.user_id}`)
                    }
                  }}
                />
                <div className={cn('flex shrink-0 items-center justify-between')}>
                  <FollowButton
                    className={cn('mr-[52px] h-[45px] min-w-[90px] rounded-[7px] border-0')}
                    userId={item.user_id}
                    showChat
                    chatClassName='w-[37px] h-[37px]'
                  />
                </div>
              </div>
            )
          })}
          <LoadingState show={Boolean(loadingMore)} className='g-loading-fixed-bottom bottom-[20px]' />
        </>
      ) : loading ? (
        <LoadingState show={true} className='g-loading-fixed-bottom' size='20px' />
      ) : showHistory ? (
        <EmptyState className='fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2' iconName='PeopleEmpty' text='Search for author' />
      ) : (
        <EmptyState className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2' iconName='PeopleEmpty' text='No results were found' />
      )}
    </div>
  )
}

export default PeopleResult
